import { Erod, TC, Ty } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import {
  useForm,
  FormProvider,
  useFormContext,
  useFieldArray,
} from 'react-hook-form';
import { Input } from '../../../components/ui/input';
import { useCallback } from 'react';
import _ from 'lodash';
import { defaultFormValuesOfSchema, FormValues } from './form-values';
import { Button } from '@cotera/client/app/components';
import { InputTypeSelector } from './input-type-selector';
import { TemplatedExpressionEditor } from '@cotera/client/app/components/eraql-editor';
import { DG } from '@cotera/sdk/core';
import {
  ErodSchemaFormMetaProvider,
  useErodSchemaFormMeta,
} from './erod-schema-form-meta';
import { ItemHeader, ShadcnSelect } from './components';
import {
  Combobox,
  ComboboxItem,
} from '@cotera/client/v0/components/ui/combobox';

/**
 * The top-level Erod Schema Form.
 *
 * Renders a form corresponding to the schema.
 *
 * Pre-fills the form with values corresponding to the initial expression,
 * if one is supplied.
 *
 * Invokes onChange with the new well-formed expression anytime
 * a user's form change results in a well-formed expression.
 */
export const ErodSchemaForm: React.FC<{
  schema: Erod.Def<any>;
  initialFormValues?: FormValues;
  onChange: (newFormValues: FormValues) => void;

  /** A parser to lint EraQL fields. */
  lintingParser: DG.Graph.RelativeParser;

  colRefAttrs: Record<string, Ty.ExtendedAttributeType>;
}> = ({ schema, initialFormValues, onChange, lintingParser, colRefAttrs }) => {
  const defaultValues = initialFormValues ?? defaultFormValuesOfSchema(schema);

  const methods = useForm<any>({
    defaultValues,
  });

  const callback = useCallback(
    (formValues: FormValues) => {
      onChange(formValues);
    },
    [onChange]
  );

  methods.subscribe({
    callback: (_data) => {
      // For some reason `_data` (the input to this function) is stale by one character. However, `methods.watch()` is perfectly fresh.
      // So we use methods.watch() instead of `_data.values`.
      // Very low priority TODO: Figure out why _data is stale by one-character, resolve that, and then pass _data.values to the callback.
      callback(methods.watch());
    },
  });

  return (
    <ErodSchemaFormMetaProvider value={{ lintingParser, colRefAttrs }}>
      <FormProvider {...methods}>
        <RenderFormNode schema={schema} path={[]} />
      </FormProvider>
    </ErodSchemaFormMetaProvider>
  );
};

const RenderFormNode: React.FC<{
  path: string[];
  schema: Erod.Def<any>;
}> = ({ path, schema }) => {
  const { setValue, watch } = useFormContext();

  const formValues: FormValues =
    path.length === 0 ? watch() : watch(path.join('.'));

  if (formValues === undefined) {
    return <></>;
  }

  return (
    <div className="flex items-start gap-2">
      <InputTypeSelector
        formValuesType={formValues.t}
        onChange={(newBaseValueType) =>
          setValue([...path, 't'].join('.'), newBaseValueType)
        }
      />
      {formValues.t === 'fieldTree' ? (
        <RenderFieldTree path={[...path, 'fieldTree']} schema={schema} />
      ) : formValues.t === 'eraQL' ? (
        <RenderEraQLFormField path={[...path, 'eraQL']} schema={schema} />
      ) : formValues.t === 'colRef' ? (
        <RenderColRefFormField path={[...path, 'colRef']} schema={schema} />
      ) : (
        Assert.unreachable(formValues.t)
      )}
    </div>
  );
};

/**
 * Recursively render a form.
 */
const optionForDiscriminatorValue = (
  discriminator: string,
  value: string | null,
  options: readonly Erod.Def<unknown>[]
): Erod.Def<unknown> => {
  const firstOption = Assert.assertExists(options[0]);
  if (value === null || value === '') {
    return firstOption;
  }
  const matching = options.find((opt) => {
    if (opt.ty.ty.k !== 'struct') {
      return false;
    }
    const fieldTy = opt.ty.ty.fields[discriminator];
    if (fieldTy === undefined) {
      return false;
    }
    if (fieldTy.ty.k !== 'enum') {
      return false;
    }
    return fieldTy.ty.t.includes(value);
  });
  return matching ?? firstOption;
};

const discriminatorValueFromFieldTree = (
  fieldTree: { struct?: Record<string, FormValues> },
  discriminator: string
): string | null => {
  if (fieldTree.struct === undefined) {
    return null;
  }
  const discriminatorFormValues = fieldTree.struct[discriminator];
  if (discriminatorFormValues === undefined) {
    return null;
  }
  if (discriminatorFormValues.t !== 'fieldTree') {
    return null;
  }
  const sub = discriminatorFormValues.fieldTree;
  if (!('baseValue' in sub)) {
    return null;
  }
  return sub.baseValue;
};

const RenderFieldTree: React.FC<{
  path: string[];
  schema: Erod.Def<any>;
}> = ({ path, schema }) => {
  const { watch } = useFormContext();

  Assert.assert(
    schema.shape.t !== 'record' && schema.shape.t !== 'recordWithKey',
    NoSchemaRecordMsg
  );

  switch (schema.shape.t) {
    case 'discriminatedUnion': {
      const { discriminator, options } = schema.shape;
      const fieldTree = watch(path.join('.'));
      const discriminatorValue =
        fieldTree !== undefined &&
        typeof fieldTree === 'object' &&
        'struct' in fieldTree
          ? discriminatorValueFromFieldTree(fieldTree, discriminator)
          : null;
      const option = optionForDiscriminatorValue(
        discriminator,
        discriminatorValue,
        options
      );
      return <RenderFieldTree path={path} schema={option} />;
    }

    case 'lazy': {
      const innerSchema = schema.shape.get();
      return <RenderFieldTree path={path} schema={innerSchema} />;
    }

    case 'struct': {
      const { fieldsSchema } = schema.shape;

      return (
        <div className="border-l border-border px-4 pb-4 space-y-4">
          {Object.entries(fieldsSchema).map(([fieldName, fieldSchema]) => (
            <div key={fieldName}>
              <ItemHeader
                displayName={fieldName}
                toolTipText={Ty.displayTy(fieldSchema.ty)}
                optional={fieldSchema.isNullable}
                description={fieldSchema.description}
              />
              <RenderFormNode
                schema={fieldSchema}
                path={[...path, 'struct', fieldName]}
              />
            </div>
          ))}
        </div>
      );
    }

    case 'array':
      return <RenderFormArray path={[...path, 'array']} schema={schema} />;

    case 'base':
      return (
        <RenderStringFormField path={[...path, 'baseValue']} schema={schema} />
      );

    default:
      return Assert.unreachable(schema.shape);
  }
};

/**
 * Renders an array of sub-forms for an array schema.
 *
 * Requires: {schema.shape.t === 'array'}
 */
const RenderFormArray: React.FC<{
  path: string[];
  schema: Erod.Def<any>;
}> = ({ path, schema }) => {
  Assert.assert(schema.shape.t === 'array');

  const { elementSchema } = schema.shape;

  const { fields, append, remove } = useFieldArray({ name: path.join('.') });

  return (
    <div>
      {fields.map((field, idx) => (
        <div key={field.id} className="flex items-end gap-2 pb-1">
          <RenderFormNode
            schema={elementSchema}
            path={[...path, idx.toString()]}
          />
          <Button type="button" icon="minus" onClick={() => remove(idx)} />
        </div>
      ))}
      <Button
        type="button"
        icon="plus"
        onClick={() => append(defaultFormValuesOfSchema(elementSchema))}
      />
    </div>
  );
};

/**
 *
 * requires: schema have shape.t === 'base
 */
const RenderStringFormField: React.FC<{
  path: string[];
  schema: Erod.Def<unknown>;
}> = ({ path, schema }) => {
  // by precondition
  Assert.assert(schema.shape.t === 'base');
  // by schema.shape specs
  Assert.assert(
    schema.ty.ty.k === 'primitive' ||
      schema.ty.ty.k === 'enum' ||
      schema.ty.ty.k === 'range'
  );
  Assert.assert(schema.ty.ty.k !== 'range', NoSchemaRangeMsg);

  const { register } = useFormContext();

  switch (schema.ty.ty.k) {
    case 'primitive': {
      Assert.assert(schema.ty.ty.t !== 'super', NoSchemaSuperMsg);
      Assert.assert(
        schema.ty.ty.t !== 'day' &&
          schema.ty.ty.t !== 'month' &&
          schema.ty.ty.t !== 'year',
        NoSchemaDayMonthYearMsg
      );

      switch (schema.ty.ty.t) {
        case 'string': {
          return <Input className="w-auto" {...register(path.join('.'))} />;
        }

        case 'int': {
          // TODO: isValidForLLMToolCall() breaks for zod.number().int(). Fix that
          return (
            <Input
              className="w-auto"
              {...register(path.join('.'), {
                validate: (input) => Number.isInteger(Number(input)),
              })}
            />
          );
        }

        case 'float': {
          return (
            <Input
              className="w-auto"
              {...register(path.join('.'), {
                validate: (input) => !Number.isNaN(Number(input)),
              })}
            />
          );
        }

        case 'boolean': {
          return <RenderBooleanFormField path={path} schema={schema} />;
        }

        case 'timestamp': {
          return <RenderTimestampFormField path={path} schema={schema} />;
        }

        default:
          return Assert.unreachable(schema.ty.ty.t);
      }
    }
    case 'enum': {
      return <RenderEnumFormField path={path} schema={schema} />;
    }

    default:
      return Assert.unreachable(schema.ty.ty);
  }
};

const RenderBooleanFormField: React.FC<{
  path: string[];
  schema: Erod.Def<any>;
}> = ({ path, schema }) => {
  Assert.assert(schema.ty.ty.k === 'primitive' && schema.ty.ty.t === 'boolean');
  const options: [value: string, label: string][] = [
    ['true', 'true'],
    ['false', 'false'],
  ];

  const { watch, setValue } = useFormContext();

  const pathStr = path.join('.');
  const initialValue = watch(pathStr);

  const onChange = useCallback(
    (newValue: string) => {
      setValue(pathStr, newValue);
    },
    [setValue, pathStr]
  );

  return (
    <ShadcnSelect
      options={options}
      initialValue={initialValue}
      placeHolder="Select"
      required={!schema.isNullable}
      onChange={onChange}
    />
  );
};

const RenderEnumFormField: React.FC<{
  path: string[];
  schema: Erod.Def<any>;
}> = ({ path, schema }) => {
  Assert.assert(schema.ty.ty.k === 'enum');
  const options = schema.ty.ty.t.map((value) => [value, value] as const);

  const { watch, setValue } = useFormContext();

  const pathStr = path.join('.');
  const initialValue = watch(pathStr);

  const onChange = useCallback(
    (newValue: string) => {
      setValue(pathStr, newValue);
    },
    [setValue, pathStr]
  );

  return (
    <ShadcnSelect
      options={options}
      initialValue={initialValue}
      placeHolder="Select"
      required={!schema.isNullable}
      onChange={onChange}
    />
  );
};

const RenderEraQLFormField: React.FC<{
  path: string[];
  schema: Erod.Def<any>;
}> = ({ path, schema: _schema }) => {
  const { watch, setValue } = useFormContext();

  // State
  const eraQL: string = watch(path.join('.'));

  const { lintingParser } = useErodSchemaFormMeta();

  // TODO validate via schema

  return (
    <div className="min-w-[150px]">
      <TemplatedExpressionEditor
        inline={true}
        eraql={eraQL}
        parser={lintingParser}
        onChange={(eraQL) => setValue(path.join('.'), eraQL)}
        templated={false}
      />
    </div>
  );
};

const RenderColRefFormField: React.FC<{
  path: string[];
  schema: Erod.Def<any>;
}> = ({ path, schema }) => {
  const pathStr = path.join('.');

  const { watch, setValue } = useFormContext();
  const colRef: string = watch(pathStr);

  const { colRefAttrs } = useErodSchemaFormMeta();
  const viableAttrs = _.pickBy(colRefAttrs, (ty) =>
    TC.implementsTy({ subject: ty, req: schema.ty })
  );

  return (
    <Combobox
      value={colRef}
      items={Object.entries(viableAttrs).map(
        ([attrName, attrTy]): ComboboxItem => ({
          value: attrName,
          label: attrName,
          section: Ty.displayTy(attrTy),
        })
      )}
      onChange={(newColRef) => setValue(pathStr, newColRef)}
    ></Combobox>
  );
};

const RenderTimestampFormField: React.FC<{
  path: string[];
  schema: Erod.Def<unknown>;
}> = ({ path, schema }) => {
  // TODO validate _schema required-ness

  Assert.assert(
    schema.ty.ty.k === 'primitive' && schema.ty.ty.t === 'timestamp'
  );

  const { getValues, setValue } = useFormContext();

  const pathStr = path.join('.');

  // ISO string in UTC or empty
  const initialFormValue = getValues(pathStr);

  Assert.assert(typeof initialFormValue === 'string');
  Assert.assert(initialFormValue === '' || isUtcISO(initialFormValue));

  // DatePicker state (HTML "datetime-local" string representing local time in UTC or empty)
  const initialDateTimeLocal =
    initialFormValue && utcDateTimeLocalOfUtcIso(initialFormValue);

  return (
    <input
      type="datetime-local"
      value={initialDateTimeLocal}
      onChange={(utcDateTimeLocal) =>
        setValue(
          pathStr,
          utcIsoOfUtcDateTimeLocal(utcDateTimeLocal.target.value)
        )
      }
    />
  );
};

export const NoSchemaSuperMsg =
  'Toolcall Contracts may not specify params with "any"/"super" schemas.';
export const NoSchemaRangeMsg =
  'Range schema not yet supported (and impossible to create).';
export const NoSchemaRecordMsg =
  'Toolcall Contracts may not specify params with record schemas.';
export const NoSchemaDayMonthYearMsg =
  'Day/Month/Year schema not yet supported (and impossible to create).';

export const isUtcISO = (s: string): boolean =>
  !Number.isNaN(new Date(s).valueOf()) && s[s.length - 1] === 'Z';

const utcDateTimeLocalOfUtcIso = (utcISO: string): string => {
  Assert.assert(isUtcISO(utcISO));
  return utcISO.substring(0, utcISO.length - 1);
};

const utcIsoOfUtcDateTimeLocal = (utcDateTimeLocal: string): string => {
  Assert.assert(!utcDateTimeLocal.includes('Z'));
  return `${utcDateTimeLocal}Z`;
};
